import type { Locale } from "@zoya/i18n";

export type PublishedOption = {
  key: string;
  label: Record<Locale, string>;
  displayOrder: number;
};
export type PublishedField = {
  key: string;
  type:
    | "SELECT"
    | "RADIO"
    | "COLOR"
    | "NUMBER"
    | "TEXT"
    | "DIMENSIONS"
    | "QUANTITY";
  required: boolean;
  displayOrder: number;
  affectsPrice: boolean;
  affectsProduction: boolean;
  label: Record<Locale, string>;
  helpText?: Record<Locale, string>;
  defaultValue?: string | number | boolean;
  validation?: {
    min?: number;
    max?: number;
    integer?: boolean;
    minLength?: number;
    maxLength?: number;
  };
  visibility?: { fieldKey: string; optionKeys: string[] };
  options?: PublishedOption[];
};
export type PublishedSelection = Record<string, string | number | boolean>;

const isVisible = (field: PublishedField, selection: PublishedSelection) =>
  !field.visibility ||
  field.visibility.optionKeys.includes(
    String(selection[field.visibility.fieldKey] ?? ""),
  );

export const visibleFields = (
  fields: PublishedField[],
  selection: PublishedSelection,
) => fields.filter((field) => isVisible(field, selection));

export const initialPublishedSelection = (fields: PublishedField[]) => {
  const selection: PublishedSelection = {};
  for (const field of fields) {
    if (field.defaultValue !== undefined)
      selection[field.key] = field.defaultValue;
  }

  for (let pass = 0; pass < fields.length; pass += 1) {
    let changed = false;
    for (const field of fields) {
      if (Object.hasOwn(selection, field.key) && !isVisible(field, selection)) {
        delete selection[field.key];
        changed = true;
      }
    }
    if (!changed) break;
  }
  return selection;
};

export const validatePublishedSelection = (
  fields: PublishedField[],
  selection: PublishedSelection,
) => {
  const errors: string[] = [];
  const fieldKeys = new Set(fields.map((field) => field.key));
  for (const key of Object.keys(selection))
    if (!fieldKeys.has(key)) errors.push(`UNKNOWN_FIELD:${key}`);
  for (const field of fields) {
    const supplied = Object.hasOwn(selection, field.key);
    if (!isVisible(field, selection)) {
      if (supplied) errors.push(`HIDDEN_FIELD:${field.key}`);
      continue;
    }
    const value = selection[field.key];
    if (value === undefined || value === "") {
      if (field.required) errors.push(`REQUIRED:${field.key}`);
      continue;
    }
    if (["SELECT", "RADIO", "COLOR"].includes(field.type)) {
      if (!field.options?.some((option) => option.key === value))
        errors.push(`UNKNOWN_OPTION:${field.key}`);
    }
    if (typeof value === "number") {
      if (field.validation?.min !== undefined && value < field.validation.min)
        errors.push(`MIN:${field.key}`);
      if (field.validation?.max !== undefined && value > field.validation.max)
        errors.push(`MAX:${field.key}`);
      if (field.validation?.integer && !Number.isInteger(value))
        errors.push(`INTEGER:${field.key}`);
    }
    if (typeof value === "string") {
      if (
        field.validation?.minLength !== undefined &&
        value.length < field.validation.minLength
      )
        errors.push(`MIN_LENGTH:${field.key}`);
      if (
        field.validation?.maxLength !== undefined &&
        value.length > field.validation.maxLength
      )
        errors.push(`MAX_LENGTH:${field.key}`);
    }
  }
  return errors;
};

export const configurationSummary = (
  fields: PublishedField[],
  selection: PublishedSelection,
  locale: Locale,
) =>
  visibleFields(fields, selection)
    .filter(
      (field) =>
        selection[field.key] !== undefined && selection[field.key] !== "",
    )
    .map((field) => {
      const value = selection[field.key]!;
      const option = field.options?.find(
        (candidate) => candidate.key === value,
      );
      return {
        label: field.label[locale],
        value: option?.label[locale] ?? String(value),
      };
    });
